import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { cn } from '@/lib/utils'

// Shared presentational helpers so every TalentForge screen looks consistent.

// Minimalist page header: every page uses this. A clear title + a one-line
// subtitle that explains what the page is for (self-explanatory, no tutorial).
export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: string
  subtitle: string
  action?: ReactNode
}) {
  return (
    <div className="mb-8 flex items-start justify-between gap-4">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  )
}

// A single calm stat — used sparingly (max 3-4 per page).
// Pass `href` to make it a clickable summary→detail link.
export function StatTile({
  label,
  value,
  hint,
  href,
}: {
  label: string
  value: string | number
  hint?: string
  href?: string
}) {
  const body = (
    <>
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-2 text-2xl font-semibold tabular-nums">{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </>
  )

  if (href) {
    return (
      <Link
        to={href}
        className="group block rounded-lg border bg-card p-4 transition-colors hover:border-primary/40 hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        {body}
      </Link>
    )
  }

  return <div className="rounded-lg border bg-card p-4">{body}</div>
}

export function MatchScore({ score, className }: { score: number; className?: string }) {
  const tone =
    score >= 85
      ? 'bg-success/15 text-success'
      : score >= 70
        ? 'bg-warning/20 text-warning-foreground'
        : 'bg-muted text-muted-foreground'
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium tabular-nums',
        tone,
        className
      )}
    >
      {score}% match
    </span>
  )
}

export function Avatar({ name, className }: { name: string; className?: string }) {
  const initials = name
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()
  return (
    <span
      className={cn(
        'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary',
        className
      )}
    >
      {initials}
    </span>
  )
}

const AGENT_TONE: Record<string, string> = {
  Screening: 'bg-info/15 text-info',
  Sourcing: 'bg-primary/10 text-primary',
  'Job Posting': 'bg-warning/20 text-warning-foreground',
  Interviews: 'bg-success/15 text-success',
  'Bulk Email': 'bg-muted text-muted-foreground',
}

export function AgentBadge({ agent }: { agent: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
        AGENT_TONE[agent] ?? 'bg-muted text-muted-foreground'
      )}
    >
      {agent}
    </span>
  )
}

// How sure the AI is about a proposal it's asking you to approve.
export function ConfidencePill({ score, className }: { score: number; className?: string }) {
  const tone =
    score >= 90
      ? 'bg-success/15 text-success'
      : score >= 80
        ? 'bg-info/15 text-info'
        : 'bg-warning/20 text-warning-foreground'
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium tabular-nums',
        tone,
        className
      )}
      title="How confident the AI is in this proposal"
    >
      AI {score}% confident
    </span>
  )
}

export function SourceTag({ source }: { source: string }) {
  return (
    <span className="inline-flex items-center rounded-md border px-1.5 py-0.5 text-[11px] text-muted-foreground">
      {source}
    </span>
  )
}
